import { useState } from "react";
import { Form } from "@remix-run/react";
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input } from "@heroui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SendPriceSchema } from "app/schema/sendPrice.schema";
import { sendPricingDetailsApi } from "app/api/Pricing/pricing.api";
import { toast } from "react-toastify";

export function SendPriceModal({
  isOpen,
  onOpenChange,
  productPricingData,
  defaultValues,
}: {
  isOpen: boolean;
  onOpenChange: () => void;
  defaultValues?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  productPricingData: {
    productId: string;
    productImageUrl: string;
    productUrl: string;
    quantity: string;
    brandingOption: string;
    color: string;
    itemPrice: string;
    totalPrice: string;
    decorationType: string;
    brandingPositions: string;
  };
}) {
  const [isLoading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: defaultValues?.firstName || "",
      lastName: defaultValues?.lastName || "",
      email: defaultValues?.email || "",
      phone: defaultValues?.phone || "",
    },
    resolver: yupResolver(SendPriceSchema),
  });

  const onSubmit = (data: SendPriceSchema) => {
    setLoading(true);

    sendPricingDetailsApi({
      productId: productPricingData.productId,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone.replace(/\s+/g, ""),
      productImageUrl: productPricingData.productImageUrl,
      productUrl: `${productPricingData.productUrl}?qty=${productPricingData?.quantity}`,
      quantity: productPricingData.quantity,
      brandingOption: productPricingData.brandingOption,
      color: productPricingData.color,
      itemPrice: productPricingData.itemPrice,
      totalPrice: productPricingData.totalPrice,
      decorationType: productPricingData.decorationType,
      brandingPositions: productPricingData.brandingPositions,
    })
      .then(() => {
        toast.success("Pricing Detail sent successfully", {
          containerId: "loginToast",
        });
        setLoading(false);

        onOpenChange();
      })
      .catch((error: any) => {
        setLoading(false);
        toast.error(error?.response?.data?.message?.[0] || error.message, {
          containerId: "loginToast",
        });
      });
  };

  return (
    <Modal size="xl" isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent className="justify-center rounded-xl shadow-2xl border border-gray-200 dark:border-gray-500 dark:bg-gray-800">
        <ModalHeader className="justify-center">Send Pricing</ModalHeader>
        <ModalBody>
          <div className="flex flex-col space-y-3">
            <span className="text-center text-sm text-gray dark:text-gray-200">
              Enter your personal details below to recieve the quotation
            </span>
            <Form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  label="FIRST NAME"
                  placeholder="Enter your first name"
                  {...register("firstName")}
                  isInvalid={!!errors.firstName?.message}
                  variant="underlined"
                  disabled={!!defaultValues?.firstName}
                  isReadOnly={!!defaultValues?.firstName}
                  isDisabled={!!defaultValues?.firstName}
                  classNames={{
                    inputWrapper: " space-y-4",
                  }}
                />
                <Input
                  type="text"
                  label="LAST NAME"
                  placeholder="Enter your last name"
                  {...register("lastName")}
                  isInvalid={!!errors.lastName?.message}
                  disabled={!!defaultValues?.lastName}
                  isReadOnly={!!defaultValues?.lastName}
                  isDisabled={!!defaultValues?.lastName}
                  variant="underlined"
                  classNames={{
                    inputWrapper: " space-y-4",
                  }}
                />
                <Input
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                  {...register("email")}
                  isInvalid={!!errors.email?.message}
                  disabled={!!defaultValues?.email}
                  isReadOnly={!!defaultValues?.email}
                  isDisabled={!!defaultValues?.email}
                  variant="underlined"
                  classNames={{
                    inputWrapper: " space-y-4",
                  }}
                />
                <Input
                  type="text"
                  label="PHONE"
                  placeholder="Enter your phone number"
                  {...register("phone")}
                  isInvalid={!!errors.phone?.message}
                  variant="underlined"
                  classNames={{
                    inputWrapper: " space-y-4",
                  }}
                />
              </div>
              <div className="py-3">
                <Button type="submit" isLoading={isLoading} fullWidth color="primary">
                  Send
                </Button>
              </div>
            </Form>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
